import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Generic hook that syncs filter state with URL search params.
 *
 * - Reads initial values from URL on mount
 * - Writes changes back to URL (replaces history entry, no back-button spam)
 * - Omits default/empty values from URL to keep it clean
 * - Returns a stable setter that merges partial updates
 *
 * @param defaults - Object defining all filter keys and their default values.
 *                   Keys with value 'all' or '' are considered "empty" and omitted from URL.
 *
 * @example
 * ```tsx
 * const [filters, setFilters] = useUrlFilters({
 *   search: '',
 *   status: 'active',
 *   tab: 'normal',
 *   page: '1',
 * });
 *
 * // Reading: filters.search, filters.status, etc.
 * // Writing: setFilters({ search: 'john' })  — merges, only updates search
 * // Reset:  setFilters({ search: '', status: 'active' }) — back to defaults
 * ```
 */
export function useUrlFilters<T extends Record<string, string>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const result = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const urlValue = searchParams.get(key);
      if (urlValue !== null) {
        (result as any)[key] = urlValue;
      }
    }
    return result;
  }, [searchParams, defaults]);

  const setFilters = useCallback(
    (updates: Partial<T>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...defaults };

          // Read current values from URL
          for (const key of Object.keys(defaults)) {
            const urlValue = next.get(key);
            if (urlValue !== null) {
              (merged as any)[key] = urlValue;
            }
          }

          // Apply updates
          Object.assign(merged, updates);

          // Write back, omitting defaults/empty values
          for (const [key, value] of Object.entries(merged)) {
            const defaultVal = defaults[key];
            if (value === defaultVal || value === '' || value === 'all') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, defaults],
  );

  return [filters, setFilters] as const;
}
